import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './DraggableMediaList.css';

const DraggableMediaList = ({
  items,
  onReorder,
  onRemove,
  onSetPrimary,
  type = 'image' // 'image' or 'video'
}) => {
  const { t } = useTranslation();
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (index !== dragOverIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];

    // Remove from old position
    newItems.splice(draggedIndex, 1);
    // Insert at new position
    newItems.splice(dropIndex, 0, draggedItem);

    onReorder(newItems);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const getMediaUrl = (item) => {
    // Handle both File objects and existing images with URLs
    if (item.preview) {
      return item.preview;
    }
    if (item.url || item.image_url || item.video_url) {
      return item.url || item.image_url || item.video_url;
    }
    if (item instanceof File) {
      return URL.createObjectURL(item);
    }
    return '';
  };

  const getMediaType = (item) => {
    if (item.type === 'video' || item.video_url) return 'video';
    if (item.type === 'image' || item.image_url) return 'image';
    if (item instanceof File) {
      return item.type.startsWith('video/') ? 'video' : 'image';
    }
    return type;
  };

  return (
    <div className="draggable-media-list">
      {items.map((item, index) => {
        const mediaUrl = getMediaUrl(item);
        const mediaType = getMediaType(item);
        const isPrimary = item.is_primary || index === 0;
        const isDragging = draggedIndex === index;
        const isDragOver = dragOverIndex === index;

        return (
          <div
            key={item.id || index}
            className={`draggable-media-item ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, index)}
          >
            <div className="drag-handle">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <circle cx="7" cy="5" r="1.5" />
                <circle cx="13" cy="5" r="1.5" />
                <circle cx="7" cy="10" r="1.5" />
                <circle cx="13" cy="10" r="1.5" />
                <circle cx="7" cy="15" r="1.5" />
                <circle cx="13" cy="15" r="1.5" />
              </svg>
            </div>

            <div className="media-preview">
              {mediaType === 'video' ? (
                <video src={mediaUrl} className="media-thumbnail" />
              ) : (
                <img src={mediaUrl} alt="" className="media-thumbnail" />
              )}

              {mediaType === 'video' && (
                <div className="video-badge">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              )}
            </div>

            <div className="media-info">
              <span className="media-order">#{index + 1}</span>
              {isPrimary && index === 0 && (
                <span className="primary-badge">Principal</span>
              )}
            </div>

            <div className="media-actions">
              {!isPrimary && onSetPrimary && index !== 0 && (
                <button
                  type="button"
                  className="btn-set-primary"
                  onClick={() => onSetPrimary(index)}
                  title={t('common.setAsPrimary')}
                >
                  ⭐
                </button>
              )}
              <button
                type="button"
                className="btn-remove"
                onClick={() => onRemove(index)}
                title={t('common.remove')}
              >
                ×
              </button>
            </div>
          </div>
        );
      })}

      {items.length === 0 && (
        <div className="empty-state">
          <p>Nenhuma {type === 'video' ? 'vídeo' : 'foto'} adicionada</p>
          <p className="text-muted">Arraste e solte para reordenar</p>
        </div>
      )}
    </div>
  );
};

export default DraggableMediaList;
